import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import TableTemplate from "../../../../../Components/Table/TableTemplate";
import { STUDENTS_ALL_ENDPOINT } from "../../../../../Constants/Endpoints";
import {
  activateAdviceStudentRequest,
  getAllSchoolsSimpleRequest,
  getSchoolsListRequest,
} from "../../../../../Backend/SchoolsRequests";
import {
  bookNumberMask,
  getRandomNumber,
  objectToQueryString,
} from "../../../../../Helpers/utils/utils";
import { setToastText } from "../../../../../Reducer";
import { ErrorLevels, Roles } from "../../../../../Constants";
import {
  getReportTypeName,
  ReportTypesFilter,
} from "../../../../../Constants/ReportTypes";
import { FormSelect } from "../../../../../Components/Buttons/FormSelect/FormSelect";
import { LogsModal } from "./LogsModal/LogsModal";
import { hasValue } from "../../../../../Helpers/validations/common";
import BasicTable from "../../../../../Components/BasicTable/BasicTable";
import { StatusCell } from "../../../Student/StudentHomePage/StatusCell/StatusCell";
import "./StudentOverviewPage.scss";

const initStudent = {
  adress: "",
  city: "",
  country: "",
  customerId: "",
  email: "",
  firstName: "",
  id: "",
  initialBooks: 0,
  keepedBooks: 0,
  lastName: "",
  postalCode: "",
  preposition: "",
  reportedBooks: 0,
  role: "",
  roleNames: [],
  scannedBooks: 0,
  schoolId: "",
  schoolName: "",
  softEmail: "",
  totalBooks: 0,
  userName: "",
};

export function StudentOverviewPage() {
  const dispatch = useDispatch();
  const tableTemplateRef = useRef();
  const tableBarcodeBooksRef = useRef();
  const [t] = useTranslation();
  const location = useLocation();
  const [queryParams, setQueryParams] = useState({});
  const userData = useSelector((state) => state.user.credentials);
  const [columns] = useState([
    {
      name: "schoolNumber",
      filterName: "school.schoolNumber",
      sortName: "school.schoolNumber",
      size: "small-s",
      headerCaption: t("SchoolNumber"),
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "schoolName",
      filterName: "school.name",
      sortName: "school.name",
      headerCaption: t("School"),
      canSort: true,
      canFilter: false,
      canEdit: false,
    },
    {
      name: "firstName",
      headerCaption: t("Name"),
      canSort: true,
      canFilter: true,
      canEdit: false,
      caption: (x) => (
        <div
          className="student-name-wrapper"
          title={`${x.firstName} ${x.preposition} ${x.lastName}`}
        >
          {`${x.firstName} ${x.preposition} ${x.lastName}`}
        </div>
      ),
    },
    // {
    //   name: "preposition",
    //   headerCaption: t("Preposition"),
    //   canSort: true,
    //   canFilter: true,
    //   canEdit: false,
    // },
    // {
    //   name: "lastName",
    //   headerCaption: t("LastName"),
    //   canSort: true,
    //   canFilter: true,
    //   canEdit: false,
    // },
    userData.role === Roles.Admin && {
      name: "softEmail",
      headerCaption: t("Email"),
      size: "large",
      canSort: true,
      canFilter: true,
      canEdit: false,
    },
    {
      name: "showAdvice",
      headerCaption: t("Notification"),
      canSort: true,
      canFilter: false,
      canEdit: false,
      size: "small-s",
      textAlign: "center",
      caption: (x) => (
        <span className="is-checkbox">
          {x.showAdvice ? "check_box" : "check_box_outline_blank"}
        </span>
      ),
    },
    {
      name: "totalBooks",
      size: "big",
      headerCaption: (
        <div className="books-wrapper-header">
          <div className="books-total">{t("Total")}</div>
          <div className="books-return">{t("Returned")}</div>
          <div className="books-report">{t("Reported")}</div>
        </div>
      ),
      caption: (x) => (
        <div className="books-wrapper">
          <div className="books-total">{x.totalBooks}</div>
          <div className="books-return">{x.scannedBooks}</div>
          <div className="books-report">{x.reportedBooks}</div>
        </div>
      ),
    },
  ]);

  const [barcodeBooksColumns] = useState([
    {
      name: "book.title",
      headerCaption: t("Title"),
      size: "big",
      canSort: false,
      canFilter: false,
      canEdit: false,
    },
    {
      name: "state",
      headerCaption: t("Status"),
      caption: (x) => (
        <StatusCell book={{ ...x }} blockedBooks={true} updateBook={null} />
      ),
      canSort: false,
      canFilter: false,
      canEdit: false,
    },
    {
      name: "reason",
      headerCaption: t("Reason"),
      size: "large",
      caption: (x) => (
        <div className="book-status" title={t(getReportTypeName(x.state))}>
          {x.state === 0 && "-"}
          {x.state === 1 && "-"}
          {x.state === 2 && t(getReportTypeName(x.reportType))}
          {x.state === 3 && t("KeepedSummertask")}
        </div>
      ),
      canSort: false,
      canFilter: false,
      canEdit: false,
    },
    {
      name: "book.isbn",
      headerCaption: t("ISBN"),
      canSort: false,
      canFilter: false,
      canEdit: false,
    },
    {
      name: "book.bookNumber",
      headerCaption: t("BookNumber"),
      size: "normal-x",
      textAlign: "right",
      caption: (x) => bookNumberMask(x.book?.bookNumber),
      canSort: false,
      canFilter: false,
      canEdit: false,
    },
  ]);

  const [showLogsModal, setShowLogsModal] = useState(false);
  const [activeStudent, setActiveStudent] = useState(initStudent);
  const [schoolList, setSchoolList] = useState([]);
  const [reportTypeSelected, setReportTypeSelected] = useState("");
  const [schoolSelected, setSchoolSelected] = useState("");

  useEffect(() => {
    if (userData.role === 0) {
      getAllSchoolList();
    } else {
      getManagerSchoolList();
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {};

    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }

    setQueryParams(params);
  }, [location.search]);

  useEffect(() => {
    if (hasValue(queryParams.reportType)) {
      setReportTypeSelected(queryParams.reportType);
    }
    if (hasValue(queryParams.schoolId)) {
      setSchoolSelected(queryParams.schoolId);
    }
  }, [queryParams]);

  const getAllSchoolList = async () => {
    const resp = await getAllSchoolsSimpleRequest();
    if (resp.success) {
      const scList = resp.data.map((sc) => ({
        id: sc.id,
        name: sc.schoolNumber + " - " + sc.name,
      }));
      setSchoolList([
        {
          id: "",
          name: t("All"),
        },
        ...scList,
      ]);
    }
  };

  const getManagerSchoolList = async () => {
    const resp = await getSchoolsListRequest(userData.id);
    if (resp.success) {
      const scList = resp.data.map((sc) => ({
        id: sc.id,
        name: sc.schoolNumber + " - " + sc.name,
      }));
      setSchoolList([
        {
          id: "",
          name: t("All"),
        },
        ...scList,
      ]);
    }
  };

  const toggleViewModal = async (row) => {
    setActiveStudent(row);
    setShowLogsModal(true);
  };

  const enableNotification = async (id) => {
    const resp = await activateAdviceStudentRequest(
      objectToQueryString({ userId: id }, true)
    );
    if (resp.success) {
      dispatch(
        setToastText({
          text: t("NotificationEnabled"),
          level: ErrorLevels.Success,
        })
      );
      tableTemplateRef.current.getItems();
    }
  };

  const clearExtraFiltersParams = () => {
    setReportTypeSelected("");
    setSchoolSelected("");
  };

  const subInfoElement = (row, i) => {
    if (row.barcodeBooks && row.barcodeBooks.length > 0) {
      return (
        <React.Fragment>
          <div className="sub-info-title">{`${t("Books")} (${
            row.barcodeBooks.length
          })`}</div>
          <BasicTable
            key={getRandomNumber() + "-sub-info-" + i}
            columns={barcodeBooksColumns}
            ref={tableBarcodeBooksRef}
            datasource={row.barcodeBooks}
          />
        </React.Fragment>
      );
    } else {
      return <div className="no-books-table">{t("StudentHasNoBooks")}</div>;
    }
  };

  return (
    <div className="student-overview-page">
      {schoolList.length > 0 && (
        <TableTemplate
          ref={tableTemplateRef}
          title={t("StudentOverview")}
          subInfo={{
            show: true,
            subInfoElement: subInfoElement,
            displayOnlyOne: true,
          }}
          subInfoElement={subInfoElement}
          endpoint={STUDENTS_ALL_ENDPOINT}
          columns={columns}
          uniqueId="id"
          paginator={true}
          clearExtraFiltersParams={clearExtraFiltersParams}
          extraFilters={[
            {
              name: "reportTypeEnum",
              headerCaption: t("ReportType"),
              value: reportTypeSelected,
              component: (
                <FormSelect
                  placeholder={t("ReportType")}
                  type="text"
                  options={ReportTypesFilter}
                  value={reportTypeSelected}
                  change={(x) => {
                    setReportTypeSelected(x);
                  }}
                />
              ),
            },
            {
              name: "school.id",
              headerCaption: t("School"),
              value: schoolSelected,
              component: (
                <FormSelect
                  placeholder={t("School")}
                  type="text"
                  options={schoolList}
                  value={schoolSelected}
                  change={(x) => setSchoolSelected(x)}
                />
              ),
            },
          ]}
          customActions={
            userData.role === 0
              ? [
                  {
                    name: t("Logs"),
                    action: (row) => toggleViewModal(row),
                  },
                  {
                    name: t("EnableNotification"),
                    action: (row) => enableNotification(row.id),
                    hide: (row) => row.showAdvice,
                  },
                ]
              : []
          }
        />
      )}
      {showLogsModal && (
        <LogsModal
          show={showLogsModal}
          student={activeStudent}
          onHide={() => setShowLogsModal(false)}
          submit={() => setShowLogsModal(false)}
        />
      )}
    </div>
  );
}
